import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import http from '../config/http'
import { useAuth } from '../contexts/AuthContext'

export default function AddCustomerModal({ open, onClose, onCreated }) {
  const { currentUser } = useAuth()
  const [form, setForm] = useState({ name: '', phone: '', email: '' })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!open) return undefined
    setForm({ name: '', phone: '', email: '' })
    setError('')
    const handleKeyDown = event => event.key === 'Escape' && onClose()
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [open, onClose])

  if (!open) return null

  const handleSubmit = async event => {
    event.preventDefault()
    if (!currentUser?.tenantId) {
      setError('Your session is missing the organization. Please sign in again.')
      return
    }
    try {
      setSaving(true)
      setError('')
      const { data } = await http.post('/api/customers', { ...form, tenantId: currentUser.tenantId })
      onCreated(data)
      onClose()
    } catch (requestError) {
      setError(requestError.userMessage || requestError.response?.data?.message || 'Unable to create customer. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  return createPortal(
    <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/40 p-4" onMouseDown={event => event.target === event.currentTarget && onClose()}>
      <div role="dialog" aria-modal="true" aria-labelledby="add-customer-title" className="w-full max-w-md rounded-xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
          <div><h2 id="add-customer-title" className="text-lg font-semibold text-gray-900">Add New Customer</h2><p className="mt-1 text-xs text-gray-500">Create and select a customer without leaving this page.</p></div>
          <button type="button" onClick={onClose} aria-label="Close" className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-700"><X className="h-5 w-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4 p-6">
          <div><label className="mb-2 block text-xs font-medium uppercase tracking-wider text-gray-500">Name *</label><input autoFocus required value={form.name} onChange={event => setForm(previous => ({ ...previous, name: event.target.value }))} placeholder="Customer name" className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-purple-500" /></div>
          <div><label className="mb-2 block text-xs font-medium uppercase tracking-wider text-gray-500">Phone *</label><input type="tel" required value={form.phone} onChange={event => setForm(previous => ({ ...previous, phone: event.target.value }))} placeholder="Phone number" className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-purple-500" /></div>
          <div><label className="mb-2 block text-xs font-medium uppercase tracking-wider text-gray-500">Email *</label><input type="email" required value={form.email} onChange={event => setForm(previous => ({ ...previous, email: event.target.value }))} placeholder="customer@email.com" className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-purple-500" /></div>
          {error && <p role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
          <div className="flex gap-3 border-t border-gray-100 pt-5"><button type="button" onClick={onClose} disabled={saving} className="flex-1 rounded-lg border border-gray-300 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">Cancel</button><button type="submit" disabled={saving} className="flex-1 rounded-lg bg-purple-600 py-2.5 text-sm font-medium text-white hover:bg-purple-700 disabled:cursor-not-allowed disabled:opacity-50">{saving ? 'Creating...' : 'Create Customer'}</button></div>
        </form>
      </div>
    </div>,
    document.body
  )
}
